import { __extends } from "tslib";
import { Event } from './Event';
/**
/// @eventType	flash.events.TouchEvent.TOUCH_BEGIN
[Event(name="touchBegin", type="flash.events.TouchEvent")]

/// @eventType	flash.events.TouchEvent.TOUCH_END
[Event(name="touchEnd", type="flash.events.TouchEvent")]

/// @eventType	flash.events.TouchEvent.TOUCH_MOVE
[Event(name="touchMove", type="flash.events.TouchEvent")]

/// @eventType	flash.events.TouchEvent.TOUCH_OUT
[Event(name="touchOut", type="flash.events.TouchEvent")]

/// @eventType	flash.events.TouchEvent.TOUCH_OVER
[Event(name="touchOver", type="flash.events.TouchEvent")]

/// @eventType	flash.events.TouchEvent.TOUCH_ROLL_OUT
[Event(name="touchRollOut", type="flash.events.TouchEvent")]

/// @eventType	flash.events.TouchEvent.TOUCH_ROLL_OVER
[Event(name="touchRollOver", type="flash.events.TouchEvent")]

/// @eventType	flash.events.TouchEvent.TOUCH_TAP
[Event(name="touchTap", type="flash.events.TouchEvent")]

 * The TouchEvent class lets you handle events on devices that detect user contact with
 * the device (such as a finger on a touch screen).
 * When a user interacts with a device such as a mobile phone or tablet with a touch screen, the user typically
 * touches the screen with his or her fingers or a pointing device. You can develop applications that respond to
 * basic touch events (such as a single finger tap) with the TouchEvent class.
 * Create event listeners using the event types defined in this class.
 * For user interaction with multiple points of contact
 * (such as several fingers moving across a touch screen at the same time) use
 * the related GestureEvent, PressAndTapGestureEvent, and TransformGestureEvent classes.
 * And, use the properties and methods of these classes
 * to construct event handlers that respond to the user touching the device.
 * <p class="- topic/p ">Use the Multitouch class to determine the current environment's support for touch interaction,
 * and to
 * manage the support of touch interaction if the current environment supports it.</p>
 * <p class="- topic/p "><b class="+ topic/ph hi-d/b ">Note:</b>
 * When objects are nested on the display list, touch events target the deepest possible
 * nested object that is visible in the display list. This object is called the target node. To have a target node's
 * ancestor (an object containing the target node in the display list) receive notification of a touch event, use
 * <codeph class="+ topic/ph pr-d/codeph ">EventDispatcher.addEventListener()</codeph>
 * on the ancestor node with the type parameter set to the specific
 * touch event you want to detect.</p>
 */
var TouchEvent = /** @class */ (function (_super) {
    __extends(TouchEvent, _super);
    /**
     * Creates an Event object that contains information about touch events.
     * Event objects are passed as parameters to event listeners.
     * @param	type	The type of the event. Possible values are: TouchEvent.TOUCH_BEGIN,
     *   TouchEvent.TOUCH_END, TouchEvent.TOUCH_MOVE,
     *   TouchEvent.TOUCH_OUT, TouchEvent.TOUCH_OVER,
     *   TouchEvent.TOUCH_ROLL_OUT, TouchEvent.TOUCH_ROLL_OVER,
     *   and TouchEvent.TOUCH_TAP.
     * @param	bubbles	Determines whether the Event object participates in the bubbling phase of the event flow.
     * @param	cancelable	Determines whether the Event object can be canceled.
     * @param	touchPointID	A unique identification number (as an int) assigned to the touch point.
     * @param	isPrimaryTouchPoint	Indicates whether the first point of contact is mapped to mouse events.
     * @param	localX	The horizontal coordinate at which the event occurred relative to the containing sprite.
     * @param	localY	The vertical coordinate at which the event occurred relative to the containing sprite.
     * @param	sizeX	Width of the contact area.
     * @param	sizeY	Height of the contact area.
     * @param	pressure	A value between 0.0 and 1.0 indicating force of the contact with the device.
     *   If the device does not support detecting the pressure, the value is 1.0.
     * @param	relatedObject	The complementary InteractiveObject instance that is affected by the event.
     * For example, when a touchOut event occurs,
     *   relatedObject represents the display list object to which the pointing device now points.
     * @param	ctrlKey	On Windows or Linux, indicates whether the Ctrl key is activated.
     * On Mac, indicates whether either the Ctrl key or the Command key is activated.
     * @param	altKey	Indicates whether the Alt key is activated (Windows or Linux only).
     * @param	shiftKey	Indicates whether the Shift key is activated.
     * @param	commandKey	(AIR only) Indicates whether the Command key is activated (Mac only).
     * This parameter is for Adobe AIR only; do not set it for Flash Player content.
     * @param	controlKey	(AIR only) Indicates whether the Control or Ctrl key is activated.
     * This parameter is for Adobe AIR only; do not set it for Flash Player content.

     */
    function TouchEvent(type, bubbles, cancelable, touchPointID, isPrimaryTouchPoint, localX, localY, sizeX, sizeY, pressure, relatedObject, ctrlKey, altKey, shiftKey) {
        if (bubbles === void 0) { bubbles = true; }
        if (cancelable === void 0) { cancelable = false; }
        if (touchPointID === void 0) { touchPointID = 0; }
        if (isPrimaryTouchPoint === void 0) { isPrimaryTouchPoint = false; }
        if (localX === void 0) { localX = NaN; }
        if (localY === void 0) { localY = NaN; }
        if (sizeX === void 0) { sizeX = NaN; }
        if (sizeY === void 0) { sizeY = NaN; }
        if (pressure === void 0) { pressure = NaN; }
        if (relatedObject === void 0) { relatedObject = null; }
        if (ctrlKey === void 0) { ctrlKey = false; }
        if (altKey === void 0) { altKey = false; }
        if (shiftKey === void 0) { shiftKey = false; }
        return _super.call(this, type, bubbles, cancelable) || this;
    }
    Object.defineProperty(TouchEvent.prototype, "altKey", {
        /**
         * Indicates whether the Alt key is active (true) or inactive (false).
         * Supported for Windows and Linux operating systems only.
         * @internal	Reserved in case Desktop Player wants to capture this key in a future implementation.
         *   The Option key modifier on Macintosh system must be represented using this key modifier. So far, it seems
         *   only the Windows version is hooked up.
         */
        get: function () { return false; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "ctrlKey", {
        /**
         * On Windows or Linux, indicates whether the Ctrl key is active (true) or inactive (false).
         * On Macintosh, indicates whether either the Control key or the Command key is activated.
         */
        get: function () { return false; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "isPrimaryTouchPoint", {
        /**
         * Indicates whether the first point of contact is mapped to mouse events.
         */
        get: function () { return false; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "isRelatedObjectInaccessible", {
        /**
         * If true, the relatedObject property is set to null for
         * reasons related to security sandboxes.  If the nominal value of relatedObject is a reference to a
         * DisplayObject in another sandbox, relatedObject is set to
         * null unless there is permission in both directions across this sandbox boundary. Permission is
         * established by calling Security.allowDomain() from a SWF file, or by providing
         * a policy file from the server of an image file, and setting the LoaderContext.checkPolicyFile
         * property when loading the image.
         */
        get: function () { return false; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "localX", {
        /**
         * The horizontal coordinate at which the event occurred relative to the containing sprite.
         */
        get: function () { return 0; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "localY", {
        /**
         * The vertical coordinate at which the event occurred relative to the containing sprite.
         */
        get: function () { return 0; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "pressure", {
        /**
         * A value between 0.0 and 1.0 indicating force of the contact with the device.
         * If the device does not support detecting the pressure, the value is 1.0.
         */
        get: function () { return 0; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "relatedObject", {
        /**
         * A reference to a display list object that is related to the event. For example, when a touchOut event occurs,
         * relatedObject represents the display list object to which the pointing device now points.
         * This property applies to the touchOut, touchOver, touchRollOut, and touchRollOver events.
         * The value of this property can be null in two circumstances: if there is no related object,
         * or there is a related object, but it is in a security sandbox to which you don't have access.
         * Use the isRelatedObjectInaccessible() property to determine which of these reasons applies.
         */
        get: function () {
            return null;
        },
        set: function (value) {
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "shiftKey", {
        /**
         * Indicates whether the Shift key is active (true) or inactive
         * (false).
         */
        get: function () { return false; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "sizeX", {
        /**
         * Width of the contact area.
         * @langversion	3.0
         */
        get: function () { return 0; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "sizeY", {
        /**
         * Height of the contact area.
         */
        get: function () { return 0; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "stageX", {
        /**
         * The horizontal coordinate at which the event occurred in global Stage coordinates.
         * This property is calculated when the localX property is set.
         */
        get: function () { return 0; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "stageY", {
        /**
         * The vertical coordinate at which the event occurred in global Stage coordinates.
         * This property is calculated when the localY property is set.
         */
        get: function () { return 0; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TouchEvent.prototype, "touchPointID", {
        /**
         * A unique identification number (as an int) assigned to the touch point.
         */
        get: function () { return 0; },
        set: function (value) { },
        enumerable: true,
        configurable: true
    });
    /**
     * Creates a copy of the TouchEvent object and sets the value of each property to match that of the original.
     * @return	A new TouchEvent object with property values that match those of the original.
     */
    TouchEvent.prototype.clone = function () {
        return null;
    };
    /**
     * Returns a string that contains all the properties of the TouchEvent object.
     * The string is in the following format:
     * [TouchEvent type=value bubbles=value cancelable=value ... ]
     * @return	A string that contains all the properties of the TouchEvent object.
     */
    TouchEvent.prototype.toString = function () { return ''; };
    /**
     * Instructs Flash Player or Adobe AIR to render after processing of this event completes,
     * if the display list has been modified.
     */
    TouchEvent.prototype.updateAfterEvent = function () {
    };
    /**
     * Defines the value of the type property of a TOUCH_BEGIN touch event object.
     *
     *   The dispatched TouchEvent object has the following properties:PropertyValuealtKeytrue
     * if the Alt key is active (Windows or Linux).bubblestruecancelablefalse;
     * there is no default behavior to cancel.commandKeytrue on the Mac if the Command key is active;
     * false if it is inactive. Always false on Windows.controlKeytrue if the Ctrl or Control key is active;
     * false if it is inactive.ctrlKeytrue on Windows or Linux if the Ctrl key is active.
     * true on Mac if either the Ctrl key or the Command key is active. Otherwise,
     * #false.currentTargetThe object that is actively processing the Event
     * object with an event listener.eventPhaseThe current phase in the event
     * flow.isRelatedObjectInaccessibletrue if the relatedObject property
     * is set to null because of security sandbox rules.localX
     * The horizontal coordinate at which the event occurred relative to the containing sprite.
     * localYThe vertical coordinate at which the event occurred relative to the containing sprite.pressure
     * A value between 0.0 and 1.0 indicating force of the contact with the device.
     * If the device does not support detecting the pressure, the value is 1.0.related
     * ObjectA reference to a display list object related to the event.shiftKey
     * true if the Shift key is active; false if it is inactive.sizeX
     * Width of the contact area.sizeYHeight of the contact area.stageX
     * The horizontal coordinate at which the event occurred in global stage coordinates.stageY
     * The vertical coordinate at which the event occurred in global stage coordinates.target
     * The InteractiveObject instance under the touching device.
     * The target is not always the object in the display list
     * that registered the event listener. Use the currentTarget
     * property to access the object in the display list that is currently processing the event.touchPoint
     * IDA unique identification number (as an int) assigned to the touch point.
     */
    TouchEvent.TOUCH_BEGIN = 'touchBegin';
    /**
     * Defines the value of the type property of a TOUCH_END touch event object.
     *
     *   The dispatched TouchEvent object has the following properties:PropertyValuealtKey
     * true if the Alt key is active (Windows or Linux).bubblestruecancelablefalse;
     * there is no default behavior to cancel.commandKeytrue on the Mac if the Command key is active;
     * false if it is inactive. Always false on Windows.controlKeytrue if the Ctrl or Control key is active;
     * false if it is inactive.ctrlKeytrue on Windows or Linux if the Ctrl key is active.
     * true on Mac if either the Ctrl key or the Command key is active. Otherwise, false.currentTarget
     * The object that is actively processing the Event
     * object with an event listener.eventPhaseThe current phase in the event flow.isRelatedObjectInaccessible
     * true if the relatedObject property is set to null because of security sandbox rules.localX
     * The horizontal coordinate at which the event occurred relative to the containing sprite.localY
     * The vertical coordinate at which the event occurred relative to the containing sprite.
     * pressureA value between 0.0 and 1.0 indicating force of the contact with the device.
     * If the device does not support detecting the pressure, the value is 1.0.related
     * ObjectA reference to a display list object related to the event.shiftKey
     * true if the Shift key is active; false if it is inactive.sizeX
     * Width of the contact area.sizeYHeight of the contact area.stageX
     * The horizontal coordinate at which the event occurred in global stage coordinates.stageY
     * The vertical coordinate at which the event occurred in global stage coordinates.
     * targetThe InteractiveObject instance under the touching device.
     * The target is not always the object in the display list
     * that registered the event listener. Use the currentTarget
     * property to access the object in the display list that is currently processing the event.
     * touchPointIDA unique identification number (as an int) assigned to the touch point.
     */
    TouchEvent.TOUCH_END = 'touchEnd';
    /**
     * Defines the value of the type property of a TOUCH_MOVE touch event object.
     *
     *   The dispatched TouchEvent object has the following properties:PropertyValue
     * altKeytrue if the Alt key is active (Windows or Linux).bubblestruecancelablefalse;
     * there is no default behavior to cancel.commandKeytrue on the Mac if the Command key is active;
     * false if it is inactive. Always false on Windows.controlKeytrue if the Ctrl or Control key is active;
     * false if it is inactive.ctrlKeytrue on Windows or Linux if the Ctrl key is active.
     * true on Mac if either the Ctrl key or the Command key is active. Otherwise, false.currentTarget
     * The object that is actively processing the Event
     * object with an event listener.eventPhaseThe current phase in the event flow.isRelatedObjectInaccessible
     * true if the relatedObject property is set to null because of security sandbox rules.localX
     * The horizontal coordinate at which the event occurred relative to the containing sprite.localY
     * The vertical coordinate at which the event occurred relative to the containing sprite.pressure
     * A value between 0.0 and 1.0 indicating force of the contact with the device.
     * If the device does not support detecting the pressure, the value is 1.0.related
     * ObjectA reference to a display list object related to the event.shiftKeytrue
     * if the Shift key is active; false if it is inactive.sizeXWidth of the contact area.sizeY
     * Height of the contact area.stageXThe horizontal coordinate
     * at which the event occurred in global stage coordinates.
     * stageYThe vertical coordinate at which the event occurred in global stage coordinates.
     * targetThe InteractiveObject instance under the touching device.
     * The target is not always the object in the display list
     * that registered the event listener. Use the currentTarget
     * property to access the object in the display list that is currently processing the event.
     * touchPointIDA unique identification number (as an int) assigned to the touch point.
     */
    TouchEvent.TOUCH_MOVE = 'touchMove';
    /**
     * Defines the value of the type property of a TOUCH_OUT touch event object.
     *
     *   The dispatched TouchEvent object has the following properties:
     * PropertyValuealtKeytrue if the Alt key is active (Windows or Linux).bubblestruecancelablefalse;
     * there is no default behavior to cancel.commandKeytrue on the Mac if the Command key is active;
     * false if it is inactive. Always false on Windows.controlKeytrue if the Ctrl or Control key is active;
     * false if it is inactive.ctrlKeytrue on Windows or Linux if the Ctrl key is active.
     * true on Mac if either the Ctrl key or the Command key is active. Otherwise, false.currentTarget
     * The object that is actively processing the Event
     * object with an event listener.eventPhaseThe current phase in the event flow.isRelatedObjectInaccessibletrue
     * if the relatedObject property is set to null because of security sandbox rules.localX
     * The horizontal coordinate at which the event occurred relative to the containing sprite.localY
     * The vertical coordinate at which the event occurred relative to the containing sprite.pressure
     * A value between 0.0 and 1.0 indicating force of the contact with the device.
     * If the device does not support detecting the pressure, the value is 1.0.relatedObject
     * A reference to a display list object related to the event.shiftKeytrue if the Shift key is active;
     * false if it is inactive.sizeXWidth of the contact area.sizeYHeight of the contact area.stageX
     * The horizontal coordinate at which the event occurred in global stage coordinates.stageY
     * The vertical coordinate at which the event occurred in global stage coordinates.target
     * The InteractiveObject instance under the touching device.
     * The target is not always the object in the display list
     * that registered the event listener. Use the currentTarget
     * property to access the object in the display list that is currently processing the event.touchPoint
     * IDA unique identification number (as an int) assigned to the touch point.
     */
    TouchEvent.TOUCH_OUT = 'touchOut';
    /**
     * Defines the value of the type property of a TOUCH_OVER touch event object.
     *
     *   The dispatched TouchEvent object has the following properties:PropertyValuealtKey
     * true if the Alt key is active (Windows or Linux).bubblestruecancelablefalse;
     * there is no default behavior to cancel.commandKeytrue on the Mac if the Command key is active;
     * false if it is inactive. Always false on Windows.controlKeytrue if the Ctrl or Control key is active; false
     * if it is inactive.ctrlKeytrue on Windows or Linux if the Ctrl key is active.
     * true on Mac if either the Ctrl key or the Command key is active. Otherwise,
     * false.currentTargetThe object that is actively processing the Event
     * object with an event listener.eventPhaseThe current phase in the event
     * flow.isRelatedObjectInaccessibletrue if the relatedObject property
     * is set to null because of security sandbox rules.
     * localXThe horizontal coordinate at which the event occurred relative to the containing sprite.localY
     * The vertical coordinate at which the event occurred relative to the containing sprite.pressure
     * A value between 0.0 and 1.0 indicating force of the contact with the device.
     * If the device does not support detecting the pressure, the value is 1.0.relatedObject
     * A reference to a display list object related to the event.shiftKeytrue
     * if the Shift key is active; false if it is inactive.sizeXWidth of the contact area.sizeY
     * Height of the contact area.stageXThe horizontal coordinate at which
     * the event occurred in global stage coordinates.stageYThe vertical coordinate
     * at which the event occurred in global stage coordinates.target
     * The InteractiveObject instance under the touching device.
     * The target is not always the object in the display list
     * that registered the event listener. Use the currentTarget
     * property to access the object in the display list that is currently processing the event.
     * touchPointIDA unique identification number (as an int) assigned to the touch point.
     */
    TouchEvent.TOUCH_OVER = 'touchOver';
    /**
     * Defines the value of the type property of a TOUCH_ROLL_OUT touch event object.
     *
     *   The dispatched TouchEvent object has the following properties:PropertyValuealtKeytrue
     * if the Alt key is active (Windows or Linux).bubblestruecancelablefalse; there is no default behavior to cancel.
     * commandKeytrue on the Mac if the Command key is active; false if it is inactive.
     * Always false on Windows.controlKeytrue if the Ctrl or Control key is active;
     * false if it is inactive.ctrlKeytrue on Windows or Linux if the Ctrl key is active.
     * true on Mac if either the Ctrl key or the Command key is active. Otherwise, false.currentTarget
     * The object that is actively processing the Event
     * object with an event listener.eventPhaseThe current phase in the event flow.isRelatedObjectInaccessibletrue
     * if the relatedObject property is set to null because of security sandbox rules.localX
     * The horizontal coordinate at which the event occurred relative to the containing sprite.localY
     * The vertical coordinate at which the event occurred relative to the containing sprite.pressure
     * A value between 0.0 and 1.0 indicating force of the contact with the device.
     * If the device does not support detecting the pressure, the value is 1.0.relatedObject
     * A reference to a display list object related to the event.shiftKeytrue if the Shift key is active;
     * false if it is inactive.sizeXWidth of the contact area.sizeYHeight of the contact area.stageX
     * The horizontal coordinate at which the event occurred in global stage coordinates.stageY
     * The vertical coordinate at which the event occurred in global stage coordinates.target
     * The InteractiveObject instance under the touching device.
     * The target is not always the object in the display list
     * that registered the event listener. Use the currentTarget
     * property to access the object in the display list that is currently processing the event.touchPointID
     * A unique identification number (as an int) assigned to the touch point.
     */
    TouchEvent.TOUCH_ROLL_OUT = 'touchRollOut';
    /**
     * Defines the value of the type property of a TOUCH_ROLL_OVER touch event object.
     *
     *   The dispatched TouchEvent object has the following properties:PropertyValuealtKey
     * true if the Alt key is active (Windows or Linux).bubblestruecancelablefalse;
     * #there is no default behavior to cancel.commandKeytrue on the Mac if the Command key is active;
     * false if it is inactive. Always false on Windows.controlKeytrue if the Ctrl or Control key is active;
     * false if it is inactive.ctrlKeytrue on Windows or Linux if the Ctrl key is active.
     * true on Mac if either the Ctrl key or the Command key is active. Otherwise, false.currentTarget
     * The object that is actively processing the Event
     * object with an event listener.eventPhaseThe current phase in the event flow.isRelatedObjectInaccessibletrue
     * if the relatedObject property is set to null because of security sandbox rules.localX
     * The horizontal coordinate at which the event occurred relative to the containing sprite.localY
     * The vertical coordinate at which the event occurred relative to the containing sprite.pressure
     * A value between 0.0 and 1.0 indicating force of the contact with the device.
     * If the device does not support detecting the pressure, the value is 1.0.relatedObject
     * A reference to a display list object related to the event.shiftKeytrue if the Shift key is active;
     * false if it is inactive.sizeXWidth of the contact area.sizeYHeight of the contact area.stageX
     * The horizontal coordinate at which the event occurred in global stage coordinates.stageY
     * The vertical coordinate at which the event occurred in global stage coordinates.target
     * The InteractiveObject instance under the touching device.
     * The target is not always the object in the display list
     * that registered the event listener. Use the currentTarget
     * property to access the object in the display list that is currently processing the event.touchPointID
     * A unique identification number (as an int) assigned to the touch point.
     */
    TouchEvent.TOUCH_ROLL_OVER = 'touchRollOver';
    /**
     * Defines the value of the type property of a TOUCH_TAP touch event object.
     *
     *   The dispatched TouchEvent object has the following properties:PropertyValuealtKeytrue
     * if the Alt key is active (Windows or Linux).bubblestruecancelablefalse;
     * there is no default behavior to cancel.commandKeytrue on the Mac if the Command key is active;
     * false if it is inactive. Always false on Windows.controlKeytrue if the Ctrl or Control key is active;
     * false if it is inactive.ctrlKeytrue on Windows or Linux if the Ctrl key is active.
     * true on Mac if either the Ctrl key or the Command key is active. Otherwise, false.currentTarget
     * The object that is actively processing the Event
     * object with an event listener.eventPhaseThe current phase in the event flow.isRelatedObjectInaccessibletrue
     * if the relatedObject property is set to null because of security sandbox rules.localX
     * The horizontal coordinate at which the event occurred relative to the containing sprite.localY
     * The vertical coordinate at which the event occurred relative to the containing sprite.
     * pressureA value between 0.0 and 1.0 indicating force of the contact with the device.
     * If the device does not support detecting the pressure,
     * the value is 1.0.relatedObjectA reference to a display list object related to the event.shiftKey
     * true if the Shift key is active; false if it is inactive.sizeXWidth of the contact area.sizeY
     * Height of the contact area.stageX
     * The horizontal coordinate at which the event occurred in global stage coordinates.
     * stageYThe vertical coordinate at which the event occurred in global stage coordinates.target
     * The InteractiveObject instance under the touching device.
     * The target is not always the object in the display list
     * that registered the event listener. Use the currentTarget
     * property to access the object in the display list that is currently processing the event.touchPointID
     * A unique identification number (as an int) assigned to the touch point.
     */
    TouchEvent.TOUCH_TAP = 'touchTap';
    return TouchEvent;
}(Event));
export { TouchEvent };
